/**
 * SoleadLab 2.0 — Asset Node API Client
 *
 * B3.5: Read surface for the asset_nodes table.
 *
 * Asset nodes are the canonical topology tree derived from base_data
 * by the asset graph sync service. They are read-only from the frontend
 * perspective — the tree is authoritative and managed by the backend.
 *
 * Transforms:
 *   AssetNodeEntityRow (snake_case DB shape) → AssetNode (camelCase frontend shape)
 *   topology_json  → topology  (parsed Record)
 *   geo_centroid_json → geoCentroid  (parsed [lon, lat] | null)
 */

import { apiFetch } from './apiFetch';

// ─── Entity Row (DB/wire shape) ──────────────────────────────────────────────

export interface AssetNodeEntityRow {
  id: string;
  site_id: string;
  asset_type: string;
  canonical_label: string;
  display_name: string | null;
  topology_json: string | null;
  geo_centroid_json: string | null;
  parent_asset_id: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

// ─── Frontend Shape ──────────────────────────────────────────────────────────

export type AssetType =
  | 'site'
  | 'station'
  | 'inverter'
  | 'mppt'
  | 'dccb'
  | 'string'
  | 'module'
  | (string & Record<never, never>); // allow custom types

export interface AssetNode {
  id: string;
  siteId: string;
  assetType: AssetType;
  canonicalLabel: string;
  displayName: string | null;
  topology: Record<string, unknown>;
  geoCentroid: [number, number] | null;
  parentAssetId: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssetNodeFilters {
  assetType?: AssetType;
  parentAssetId?: string;
}

// ─── Transform ───────────────────────────────────────────────────────────────

function entityRowToAssetNode(row: AssetNodeEntityRow): AssetNode {
  let topology: Record<string, unknown> = {};
  if (row.topology_json) {
    try { topology = JSON.parse(row.topology_json); } catch { /* keep empty */ }
  }

  let geoCentroid: [number, number] | null = null;
  if (row.geo_centroid_json) {
    try {
      const parsed = JSON.parse(row.geo_centroid_json) as unknown;
      if (Array.isArray(parsed) && parsed.length >= 2) {
        geoCentroid = [parsed[0] as number, parsed[1] as number];
      }
    } catch { /* keep null */ }
  }

  return {
    id: row.id,
    siteId: row.site_id,
    assetType: row.asset_type,
    canonicalLabel: row.canonical_label,
    displayName: row.display_name ?? null,
    topology,
    geoCentroid,
    parentAssetId: row.parent_asset_id ?? null,
    version: row.version,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Fetch all asset nodes for a site (auto-paginated).
 * Optionally filter by asset_type or parent_asset_id.
 */
export async function fetchAssetNodes(
  siteId: string,
  filters?: AssetNodeFilters,
): Promise<{ items: AssetNode[]; total: number }> {
  const params = new URLSearchParams({ limit: '500' });
  if (filters?.assetType) params.set('asset_type', filters.assetType);
  if (filters?.parentAssetId) params.set('parent_asset_id', filters.parentAssetId);

  let page = 1;
  const allItems: AssetNode[] = [];
  let total = 0;

  while (true) {
    params.set('page', String(page));
    const result = await apiFetch<{
      items: AssetNodeEntityRow[];
      total: number;
      page: number;
      limit: number;
      hasMore: boolean;
    }>(`/api/v2/sites/${siteId}/asset-nodes?${params}`);

    total = result.total;
    allItems.push(...result.items.map(entityRowToAssetNode));

    if (!result.hasMore) break;
    page++;
  }

  return { items: allItems, total };
}

/**
 * Fetch a single asset node by ID. Returns null on 404.
 */
export async function fetchSingleAssetNode(
  siteId: string,
  nodeId: string,
): Promise<AssetNode | null> {
  try {
    const row = await apiFetch<AssetNodeEntityRow>(
      `/api/v2/sites/${siteId}/asset-nodes/${nodeId}`,
    );
    return entityRowToAssetNode(row);
  } catch (err) {
    if (err instanceof Error && (err as Error & { status: number }).status === 404) {
      return null;
    }
    throw err;
  }
}
